import { useParams } from 'react-router-dom'
import { useGlobalErrorHandler, useMutation } from '@src/hooks'
import { SystemService } from '@src/domain/services'
import { Category } from '@src/domain/repositories/system.repo';
import { useDispatch, useSelector, systemActions } from '@src/store'
import { useEffect, useState } from 'react'
import { ModalResolveType, useModal } from '@src/lib/modal-manager'
import { useDialog } from '@src/components/dialog'
import { useSpinner } from '@src/components/spinner';
import { editModalMap } from './config'


export function SystemViewModel() {
  const params = useParams()
  const category = params.category as Category
  const [currentPage, setPage] = useState(1)
  const [keyword, setKeyword] = useState('')
  const dispatch = useDispatch()
  const { showModal } = useModal()
  const { dialog } = useDialog()
  const { spinner } = useSpinner()
  const { globalErrorHandler } = useGlobalErrorHandler()

  const { mutate: getItemByCategroy } = useMutation(SystemService.getItemByCategroy)
  const { mutate: addItemByCategory } = useMutation(SystemService.addItemByCategory)
  const { mutate: updateItemByCategroy } = useMutation(SystemService.updateItemByCategroy)
  const { mutate: deleteItemByCategroy } = useMutation(SystemService.deleteItemByCategroy)
  const { mutate: getItemListByCategory } = useMutation(SystemService.getItemListByCategory)

  const { systemData } = useSelector(state => state.system)

  const fetchDefaultList = async () => {
    const itemListDto = await spinner(getItemListByCategory({ keyword: '', page: 1, category }))

    dispatch(systemActions.setSystemData(itemListDto))
    setPage(1)
    setKeyword('')
  }
  
  useEffect(() => {
    fetchDefaultList()

    return () => {
      dispatch(
        systemActions.clearSystemData()
      )
      setPage(1)
      setKeyword('')
    }
  }, [category])

  const getItemListByPageNumber = async (pageNumber: number, keyword: string) => {
    const itemListDto = await spinner(getItemListByCategory({ keyword, page: pageNumber, category }))

    dispatch(
      systemActions.setSystemData(itemListDto)
    )
  }

  const handleKeywordOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value)
  }

  const handleSearchClick = async () => {
    getItemListByPageNumber(1, keyword)
    setPage(1)
  }

  const handleNextOnClick = async () => {
    if (currentPage === systemData?.totalPages) return
    await getItemListByPageNumber(currentPage + 1, keyword)
    setPage(currentPage + 1)
  }
  
  const handlePrevOnClick = async () => {
    if (currentPage === 1) return
    await getItemListByPageNumber(currentPage - 1, keyword)
    setPage(currentPage - 1)
  }

  const handleMoveToOnClick = async (pageNumber: number) => {
    await getItemListByPageNumber(pageNumber, keyword)
    setPage(pageNumber)
  }

  const handleEditBtnOnClick = async (rowData: any) => {
    if (category === 'api-logs') return
    try {
      const { uuid } = rowData
      const itemDto = await spinner(getItemByCategroy({ category, uuid })) as any

      const result = await showModal(editModalMap[category], { dtoUuid: uuid, itemDto })

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as SystemService.GetItemByCategoryRespBo

      await spinner(updateItemByCategroy({ category, uuid, data }))

      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleAddBtnOnClick = async () => {
    if (category === 'api-logs') return
    try {
      const result = await showModal(editModalMap[category])

      if(result.type !== ModalResolveType.OK) return

      const data = result.data as SystemService.GetItemByCategoryRespBo

      await spinner(addItemByCategory({ category, data }))

      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  const handleDelBtnOnClick = async (rowData: any) => {
    const { uuid } = rowData
    const result = await dialog.confirm({
      title: '刪除確認',
      message: '是否要刪除這筆資料'
    })

    if (result.type !== ModalResolveType.OK) return

    try {
      await spinner(deleteItemByCategroy({ category, uuid }))
      await fetchDefaultList()
    } catch (error) {
      globalErrorHandler(error)
    }
  }

  return {
    category,
    currentPage,
    keyword,
    setKeyword,
    systemData,
    handleKeywordOnChange,
    handleSearchClick,
    handleNextOnClick,
    handlePrevOnClick,
    handleMoveToOnClick,
    handleAddBtnOnClick,
    handleEditBtnOnClick,
    handleDelBtnOnClick
  }
}

export type ISystemViewModel = ReturnType<typeof SystemViewModel>
